import { handleActions, combineActions } from "redux-actions";
import * as Immutable from "immutable";

import * as Actions from "./accountActions";
import {userLogoutReceived} from "../../../security";
import {BILLABLE, CREDIT, PARTIAL} from "../../../constants";

export const initialState: Immutable.Map<string, any> = Immutable.fromJS({
  isFetchingData: false,
  error: "",
  accounts: [],
  accountInfo: {},
  meters: [],
  locationFilters: [],
  groupFilters: [],
  billable: {
    filters: {
      location: [],
      group: [],
      meter: [],
    },
    meters: [],
  },
  credit: {
    filters: {
      location: [],
      group: [],
      meter: [],
    },
    meters: [],
  },
  partial: {
    filters: {
      location: [],
      group: [],
      meter: [],
    },
    meters: [],
    percentage: ''
  },
  grid: {
    sortColumn: '',
    sortDirection: '',
    filters: {}
  }
});

const accountReducer = handleActions(
  {
    [combineActions(Actions.fetchAccountRequested, Actions.fetchAllAccountsRequested, Actions.createAccountRequested, Actions.fetchAccountFilteredMetersRequested,
      Actions.deleteAccountRequested, Actions.addMeterToBillingAccountRequested, Actions.removeMeterFromBillingAccountRequested, Actions.configurePartialMeterPercentageRequested,
      Actions.editPartialMeterPercentageRequested)](state, action) {
      return state
        .set("isFetchingData", true)
        .set("error", "")
    },
    [combineActions(Actions.fetchAccountFailed, Actions.fetchAllAccountsFailed, Actions.createAccountFailed, Actions.fetchAccountFilteredMetersFailed,
      Actions.deleteAccountFailed, Actions.addMeterToBillingAccountFailed, Actions.removeMeterFromBillingAccountFailed, Actions.configurePartialMeterPercentageFailed,
      Actions.editPartialMeterPercentageFailed)](state, action) {
      return state
        .set("isFetchingData", false)
        .set("error", action.payload)
    },
    [Actions.fetchAllAccountsReceived](state, action) {
      return state
        .set("isFetchingData",false)
        .set("accounts", Immutable.fromJS(action.payload))
    },
    [Actions.fetchAccountReceived](state, action) {
      let updatedState = state.set("isFetchingData",false)
      if(action.payload) {
        updatedState = updatedState.update("accounts", accounts => {
          const index = accounts.findIndex(account => account.get("billingAccountId") === action.payload.billingAccountId)
          if(index >= 0) {
            return accounts.set(index, Immutable.fromJS(action.payload))
          } else {
            return accounts.push(Immutable.fromJS(action.payload))
          }
        })
      }
      return updatedState;
    },
    [Actions.fetchAccountFilteredMetersReceived](state, action) {
      return state
        .set("isFetchingData", false)
        .set("meters", Immutable.fromJS(action.payload))
    },
    [Actions.setAccountInfo](state, action) {
      return state
        .set("accountInfo", Immutable.fromJS(action.payload))
    },
    [Actions.unsetAccountInfo](state, action) {
      return state
        .set("accountInfo", Immutable.fromJS({}))
    },
    [Actions.addLocationFilter](state, action) {
      return state
        .update("locationFilters", locationFilters => locationFilters.push(action.payload))
    },
    [Actions.addGroupFilter](state, action) {
      return state
        .update("groupFilters", groupFilters => groupFilters.push(action.payload))
    },
    [Actions.setCreditableMeters](state, action) {
      return state
        .setIn([CREDIT,"meters"], Immutable.fromJS(action.payload))
    },
    [Actions.setBillableMeters](state, action) {
      return state
        .setIn([BILLABLE,"meters"], Immutable.fromJS(action.payload))
    },
    [Actions.addBillableLocationFilter](state, action) {
      return state
        .updateIn([BILLABLE, "filters", "location"], locationFilters => locationFilters.push(action.payload))
    },
    [Actions.addBillableGroupFilter](state, action) {
      return state
        .updateIn([BILLABLE, "filters", "group"], groupFilters => groupFilters.push(action.payload))
    },
    [Actions.addBillableMeterFilter](state, action) {
      return state
        .updateIn([BILLABLE, "filters", "meter"], meterFilters => meterFilters.push(action.payload))
    },
    [Actions.addCreditableLocationFilter](state, action) {
      return state
        .updateIn([CREDIT, "filters", "location"], locationFilters => locationFilters.push(action.payload))
    },
    [Actions.addCreditableGroupFilter](state, action) {
      return state
        .updateIn([CREDIT, "filters", "group"], groupFilters => groupFilters.push(action.payload))
    },
    [Actions.addCreditableMeterFilter](state, action) {
      return state
        .updateIn([CREDIT, "filters", "meter"], meterFilters => meterFilters.push(action.payload))
    },
    [Actions.addPartialLocationFilter](state, action) {
      return state
        .updateIn([PARTIAL, "filters", "location"], locationFilters => locationFilters.push(action.payload))
    },
    [Actions.addPartialGroupFilter](state, action) {
      return state
        .updateIn([PARTIAL, "filters", "group"], groupFilters => groupFilters.push(action.payload))
    },
    [Actions.addPartialMeterFilter](state, action) {
      return state
        .updateIn([PARTIAL, "filters", "meter"], meterFilters => meterFilters.push(action.payload))
    },
    [Actions.setPartialPercentage](state, action) {
      return state
        .setIn([PARTIAL, "percentage"], action.payload)
    },
    [Actions.setPartialMeters](state, action) {
      return state
        .setIn([PARTIAL,"meters"], Immutable.fromJS(action.payload))
    },
    [Actions.resetBillableMeters](state) {
      return state
        .set(BILLABLE, initialState.get(BILLABLE))
    },
    [Actions.resetCreditableMeters](state) {
      return state
        .set(CREDIT, initialState.get(CREDIT))
    },
    [Actions.resetPartialMeters](state) {
      return state
        .set(PARTIAL, initialState.get(PARTIAL))
    },
    [Actions.deleteAccountReceived](state) {
      return state
        .set("isFetchingData", false)
    },
    [combineActions(Actions.addMeterToBillingAccountReceived, Actions.removeMeterFromBillingAccountReceived, Actions.configurePartialMeterPercentageReceived, Actions.editPartialMeterPercentageReceived)](state) {
      return state
        .set("isFetchingData", false)
    },
    [Actions.unsetAccountError](state) {
      return state
        .set("error", "")
    },
    [Actions.setGridFiltersAction](state, action) {
      return state
        .setIn(["grid", "filters"], action.payload.filters)
    },
    [Actions.setGridSortAction](state, action) {
      return state
        .setIn(["grid", "sortColumn"], action.payload.sortColumn)
        .setIn(["grid", "sortDirection"], action.payload.sortDirection);
    },
    [userLogoutReceived](state, action) {
      return initialState
    }
  },
  initialState
);

export { accountReducer };



// WEBPACK FOOTER //
// ./src/features/account/store/accountReducer.js